import { api, ApiError, type IndexEntry, type IndexSettings } from '../api.ts';
import { Preview } from '../preview.ts';
import { debounce, el, field, select, toast } from '../ui.ts';

/**
 * The main index page (100): logo, the picklist of what to highlight
 * (sections and pages in service, in order), footnote rows and Fastext.
 */
export async function indexPageView(): Promise<HTMLElement> {
  const [settings, sections, assets, pages] = await Promise.all([api.index(), api.sections(), api.assets(), api.pages()]);
  const state: IndexSettings = JSON.parse(JSON.stringify(settings));
  const preview = new Preview(9);

  const logo = select([{ value: '', label: '— no logo (text banner) —' }, ...assets.map((a) => ({ value: a.id, label: `${a.name} (${a.width}×${a.height})` }))], state.logoAsset ?? '');
  const editorialName = el('input', { type: 'text', value: state.editorialName, maxlength: '20' });
  const footnotes = el('textarea', { rows: '10', spellcheck: 'false' }, state.footnotes.join('\n'));
  const entriesEl = el('ol', { class: 'entries' });

  const sectionTitle = (key: string) => sections.find((s) => s.key === key);
  const describe = (e: IndexEntry) => (e.kind === 'section' ? `${(sectionTitle(e.key)?.indexLabel ?? sectionTitle(e.key)?.title ?? e.key)} → ${sectionTitle(e.key)?.indexPage ?? '?'} (section)` : `${e.label} → ${e.page}`);

  const renderEntries = () => {
    entriesEl.replaceChildren(...state.entries.map((e, i) => el('li', {},
      el('span', {}, describe(e)),
      el('span', { class: 'entry-actions' },
        el('button', { type: 'button', disabled: i === 0, onClick: () => { [state.entries[i - 1], state.entries[i]] = [state.entries[i]!, state.entries[i - 1]!]; renderEntries(); refresh(); } }, '↑'),
        el('button', { type: 'button', disabled: i === state.entries.length - 1, onClick: () => { [state.entries[i + 1], state.entries[i]] = [state.entries[i]!, state.entries[i + 1]!]; renderEntries(); refresh(); } }, '↓'),
        el('button', { type: 'button', onClick: () => { state.entries.splice(i, 1); renderEntries(); refresh(); } }, '✕'),
      ),
    )));
  };

  // picklist: sections not yet listed + pages in service not yet listed
  const picked = () => new Set(state.entries.map((e) => (e.kind === 'section' ? `s:${e.key}` : `p:${e.page}`)));
  const pick = el('select', {});
  const refreshPick = () => {
    const have = picked();
    const opts: Array<{ value: string; label: string }> = [{ value: '', label: '— add an element —' }];
    for (const s of sections) if (!have.has(`s:${s.key}`)) opts.push({ value: `s:${s.key}`, label: `Section: ${s.title} (${s.indexPage})` });
    for (const p of pages) if (!have.has(`p:${p.page}`) && !sections.some((s) => s.indexPage === p.page || (p.page >= s.firstPage && p.page <= s.lastPage))) opts.push({ value: `p:${p.page}`, label: `Page ${p.page}: ${p.title ?? ''}` });
    opts.push({ value: 'custom', label: 'Custom page number…' });
    pick.replaceChildren(...opts.map((o) => el('option', { value: o.value }, o.label)));
  };
  pick.addEventListener('change', () => {
    const v = pick.value;
    if (!v) return;
    if (v === 'custom') {
      const n = Number(prompt('Page number (100–899)'));
      const label = prompt('Label (as shown on the index)')?.trim();
      if (n >= 100 && n <= 899 && label) state.entries.push({ kind: 'page', label, page: n });
    } else if (v.startsWith('s:')) {
      state.entries.push({ kind: 'section', key: v.slice(2) });
    } else {
      const page = Number(v.slice(2));
      const title = pages.find((p) => p.page === page)?.title ?? `Page ${page}`;
      const label = prompt('Label (as shown on the index)', title.toUpperCase().slice(0, 14))?.trim();
      if (label) state.entries.push({ kind: 'page', label, page });
    }
    renderEntries(); refreshPick(); refresh();
  });

  const keys = ['red', 'green', 'yellow', 'blue'] as const;
  const ftInputs = keys.map((k) => ({ label: el('input', { type: 'text', value: state.fastext[k][0], maxlength: '9' }), page: el('input', { type: 'number', value: String(state.fastext[k][1]), min: '100', max: '899' }) }));
  const ftRoot = el('div', { class: 'fastext-grid' }, keys.map((k, i) => el('div', { class: `ft ${k}` }, el('span', {}, k), ftInputs[i]!.label, ftInputs[i]!.page)));

  const collect = (): IndexSettings => {
    state.logoAsset = logo.value || null;
    state.editorialName = editorialName.value.trim() || 'TEXTBOX';
    state.footnotes = footnotes.value.split('\n').map((l) => l.replace(/\s+$/, ''));
    while (state.footnotes.length && state.footnotes[state.footnotes.length - 1] === '') state.footnotes.pop();
    state.fastext = { red: [ftInputs[0]!.label.value, Number(ftInputs[0]!.page.value)], green: [ftInputs[1]!.label.value, Number(ftInputs[1]!.page.value)], yellow: [ftInputs[2]!.label.value, Number(ftInputs[2]!.page.value)], blue: [ftInputs[3]!.label.value, Number(ftInputs[3]!.page.value)] };
    return state;
  };
  const refresh = debounce(async () => {
    try { preview.show(await api.preview({ kind: 'index', settings: collect() })); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'preview failed', 'error'); }
  }, 350);
  for (const i of [logo, editorialName, footnotes, ...ftInputs.flatMap((f) => [f.label, f.page])]) i.addEventListener('input', refresh);

  renderEntries(); refreshPick(); void refresh();
  return el('section', { class: 'editor' },
    el('div', { class: 'head' }, el('h1', {}, 'Index page (100)')),
    el('div', { class: 'cols' },
      el('div', { class: 'form' },
        field('Logo', logo, 'Block art placed on rows 2–5; the menu starts a row below it.'),
        field('Elements to highlight', el('div', {}, entriesEl, pick), 'Sections and pages in service, in the order shown. Two columns of LABEL.....ppp.'),
        field('Footnote rows', footnotes, 'One row per line in authoring markup: [C] cyan, [W] white, [gR] red mosaic, [#7f] block, [H] conceal… empty line = blank row. Max 40 cells.'),
        field('Fastext', ftRoot),
        field('Editorial credit name', editorialName, 'Used on the credit row of published articles ("Source: TEXTBOX / author").'),
        el('div', { class: 'actions' }, el('button', { type: 'button', class: 'primary', onClick: async () => {
          try { await api.saveIndex(collect()); toast('Saved — page 100 rebuilt'); } catch (e) { toast(e instanceof ApiError ? e.message : 'save failed', 'error'); }
        } }, 'Save')),
      ),
      preview.root,
    ),
  );
}
